/*
 * Lese-Stile der Wiki-Inhalte (Paket E-W1, G-W3 Variante A, Betreiber 13.10.2026): Schrift `text-base` mit Zeilenhöhe 7,
 * Typografie nur aus Theme-Tokens und Standardklassen für ALLE Knoten. Die Klassen hängen als Darstellungs-Attribut
 * `hornKlasse` an den Knoten (`HornKlassen` in `@horn/editor/kern`), NICHT über `@horn/editor/theme.css`. Ganze, feste
 * Ketten (nie zusammengesetzt, R8); das Dokument-Schema bleibt unberührt. Die Vorlage ist der Zusammenbau
 * `w3-a-lesen` des Gestaltungsvorschlags (Eigenbau aus Standardklassen, AW-051).
 */

/**
 * Lesebreite des Artikels (G-W3, Frage Q1): die Seite setzt sie auf den Artikel. Hier als Konstante, damit die Seite
 * (P-W3a) keine zweite Zahl erfindet.
 */
export const LESE_ARTIKEL_KLASSE = "max-w-4xl";

/** Das Lese-Element (ProseMirror-Wurzel, nicht bearbeitbar): Abstand zwischen den Blöcken, Grundfarbe (Absätze und Listen erben sie, damit Hinweisfelder ihren Ton behalten), kein Fokusrahmen. */
export const LESE_FLAECHE_KLASSE = "space-y-4 text-foreground focus:outline-hidden";

type KlassenWert = string | Partial<Record<number, string>> | ((attrs: Record<string, unknown>) => string | null);

const ZELLE_AUSRICHTUNG: Record<string, string> = {
  left: "text-start",
  center: "text-center",
  right: "text-end",
};
const ZELLE_SENKRECHT: Record<string, string> = {
  middle: "align-middle",
  bottom: "align-bottom",
};

/** Zelle mit Ausrichtung: Basiskette plus (optional) waagerecht und senkrecht, jeweils eine ganze Klasse. */
function zelle(basis: string) {
  return (attrs: Record<string, unknown>): string => {
    const waagerecht = ZELLE_AUSRICHTUNG[typeof attrs.textAlign === "string" ? attrs.textAlign : "left"] ?? "text-start";
    const senkrecht = typeof attrs.verticalAlign === "string" ? ZELLE_SENKRECHT[attrs.verticalAlign] : undefined;
    return [basis, waagerecht, senkrecht].filter(Boolean).join(" ");
  };
}

/** Klassen je Knoten- und Mark-Typ des Lesepfads. Schlüssel = TipTap-Typnamen. */
export const LESE_KNOTEN_KLASSEN: Record<string, KlassenWert> = {
  paragraph: "text-base leading-7",
  heading: {
    1: "text-xl font-semibold text-foreground",
    2: "text-lg font-semibold text-foreground",
    3: "text-base font-semibold text-foreground",
    4: "text-base font-medium text-foreground",
    5: "text-sm font-semibold text-foreground",
    6: "text-sm font-medium text-foreground",
  },
  bulletList: "list-disc list-outside ps-5 space-y-1 text-base leading-7",
  orderedList: "list-decimal list-outside ps-5 space-y-1 text-base leading-7",
  listItem: "ps-1",
  taskList: "list-none ps-0 space-y-1 text-base leading-7",
  taskItem: "flex items-start gap-x-2",
  blockquote: "relative border-s-4 border-layer-line ps-4 text-base leading-7",
  codeBlock: "bg-surface text-surface-foreground rounded-lg p-3 font-mono text-xs overflow-x-auto",
  code: "rounded-md bg-muted px-1 py-0.5 font-mono text-sm text-foreground",
  horizontalRule: "my-6 border-layer-line",
  tableRow: "border-b border-layer-line last:border-b-0",
  tableHeader: zelle("px-3 py-2 text-xs font-semibold uppercase text-muted-foreground-1 bg-muted"),
  tableCell: zelle("px-3 py-2 text-foreground"),
};
